import React from 'react';
import { captureException } from '../lib/telemetry/posthog/frontend';

/** Props for {@link ErrorBoundary}. */
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/** Internal error flag flipped by getDerivedStateFromError. */
interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Repo-wide React error boundary. Without one, any render error below the
 * root white-screens the page (worst case: a respondent mid-call). Captures
 * the error to PostHog and renders a minimal reload fallback instead.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  /** Flips the boundary into fallback mode on the next render. */
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  /** Reports the caught render error to PostHog. */
  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    captureException(error, 'ErrorBoundary.componentDidCatch', {
      componentStack: info.componentStack ?? undefined,
    });
  }

  /** Full reload: render state is unknown after a caught error. */
  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground mb-6">Something went wrong</h1>
            <button
              onClick={this.handleReload}
              className="px-6 py-3 button-accent font-bold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-volt focus:ring-offset-2 focus:ring-offset-border"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
